import { useEffect, useCallback, useRef } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { DRAG_DROP_PASTE } from "@lexical/rich-text";
import {
  $createParagraphNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isParagraphNode,
  $isRangeSelection,
  $nodesOfType,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  LexicalCommand,
  PASTE_COMMAND,
} from "lexical";
import { useSnackbar } from "src/components/Snackbar/useSnackbar";
import { $createImageNode, $isImageNode, ImageNode } from "../ImageNode";
import { useImageUploadContext } from "./ImageUploadContext";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Convert a base64 data URL to a File object
 */
function dataUrlToFile(dataUrl: string, index: number): File | null {
  const match = dataUrl.match(
    /^data:image\/(png|jpeg|jpg|gif|webp);base64,(.+)$/
  );
  if (!match) return null;

  try {
    const mimeType = `image/${match[1]}`;
    const base64Data = match[2];
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const extension = match[1] === "jpeg" ? "jpg" : match[1];
    return new File([bytes], `pasted-image-${index}.${extension}`, {
      type: mimeType,
    });
  } catch {
    return null;
  }
}

/**
 * Check if HTML contains base64 embedded images (e.g., from Google Docs)
 */
function hasBase64Images(html: string): boolean {
  return /data:image\/(png|jpeg|jpg|gif|webp);base64,/.test(html);
}

export interface InsertImagePayload {
  file: File;
}

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> =
  createCommand("INSERT_IMAGE_COMMAND");

export default function ImageUploadPlugin(): null {
  const [editor] = useLexicalComposerContext();
  const { uploadImage } = useImageUploadContext();
  const { showSnackbar } = useSnackbar();
  const pendingDataUrlUploadRef = useRef(false);

  const isValidImageFile = useCallback((file: File): boolean => {
    return file.type.startsWith("image/");
  }, []);

  const validateFileSize = useCallback(
    (file: File): boolean => {
      if (file.size > MAX_FILE_SIZE) {
        showSnackbar(
          `File "${file.name}" is too large. Maximum size is 10MB.`
        );
        return false;
      }
      return true;
    },
    [showSnackbar]
  );

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!isValidImageFile(file)) {
        return;
      }

      if (!validateFileSize(file)) {
        return;
      }

      try {
        const { url, fileName } = await uploadImage(file);

        // Insert image node
        editor.update(
          () => {
            const imageNode = $createImageNode(url, fileName);

            // Try to insert at selection first
            const selection = $getSelection();

            if ($isRangeSelection(selection)) {
              $insertNodes([imageNode]);
            } else {
              // No valid selection - append to the end of the document
              const root = $getRoot();
              const lastChild = root.getLastChild();
              // ImageNode is inline, so it must be inside a paragraph
              if (lastChild && $isParagraphNode(lastChild)) {
                // Append to the existing last paragraph
                lastChild.append(imageNode);
              } else {
                // Create a new paragraph for the image
                const paragraph = $createParagraphNode();
                paragraph.append(imageNode);
                root.append(paragraph);
              }
            }
          },
          { tag: "image-upload" }
        );
      } catch (error) {
        showSnackbar("Failed to upload image. Please try again.");
        console.error("Image upload failed:", error);
      }
    },
    [editor, uploadImage, showSnackbar, isValidImageFile, validateFileSize]
  );

  /**
   * Find and upload any ImageNodes with data: URLs
   * This is called after paste to handle embedded base64 images from Google Docs etc.
   */
  const uploadDataUrlImages = useCallback(async () => {
    // Get all ImageNodes with data: URLs
    const dataUrlImages: Array<{ key: string; src: string }> = [];

    editor.getEditorState().read(() => {
      const imageNodes = $nodesOfType(ImageNode);
      imageNodes.forEach((node) => {
        const src = node.getSrc();
        if (src.startsWith("data:image/")) {
          dataUrlImages.push({ key: node.getKey(), src });
        }
      });
    });

    if (dataUrlImages.length === 0) return;

    // Upload each image
    const uploadResults = await Promise.all(
      dataUrlImages.map(async ({ key, src }, index) => {
        const file = dataUrlToFile(src, index);
        if (!file) return { key, url: null };

        if (!validateFileSize(file)) return { key, url: null };

        try {
          const { url } = await uploadImage(file);
          return { key, url };
        } catch (error) {
          console.error(`Failed to upload image:`, error);
          return { key, url: null };
        }
      })
    );

    // Update the ImageNodes with uploaded URLs
    editor.update(
      () => {
        uploadResults.forEach(({ key, url }) => {
          if (!url) return;

          const node = $getNodeByKey(key) as ImageNode | undefined;
          if (node && $isImageNode(node)) {
            // Create a new node with the uploaded URL and replace the old one
            const newNode = $createImageNode(
              url,
              node.getAltText(),
              node.getWidth()
            );
            node.replace(newNode);
          }
        });
      },
      { tag: "image-upload" }
    );
  }, [editor, uploadImage, validateFileSize]);

  useEffect(() => {
    // Register INSERT_IMAGE_COMMAND
    const unregisterInsertCommand = editor.registerCommand(
      INSERT_IMAGE_COMMAND,
      (payload: InsertImagePayload) => {
        handleImageUpload(payload.file);
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    // Register PASTE_COMMAND with HIGH priority
    const unregisterPasteCommand = editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        // If pasting Lexical content, let Lexical handle it
        const hasLexicalContent = event.clipboardData?.getData(
          "application/x-lexical-editor"
        );
        if (hasLexicalContent) {
          return false;
        }

        // Try to get image file from clipboardData.files first (local file paste)
        const files = event.clipboardData?.files;
        let imageFile: File | null =
          files && files.length > 0
            ? (Array.from(files).find((file) =>
                file.type.startsWith("image/")
              ) ?? null)
            : null;

        // If no image in files, check clipboardData.items (for browser-copied images)
        // Browser-copied images appear in items, not files
        if (!imageFile && event.clipboardData?.items) {
          const items = Array.from(event.clipboardData.items);
          for (const item of items) {
            if (item.kind === "file" && item.type.startsWith("image/")) {
              imageFile = item.getAsFile();
              if (imageFile) break;
            }
          }
        }

        // If we found a direct image file (not from HTML), upload it
        if (imageFile) {
          event.preventDefault();
          handleImageUpload(imageFile);
          return true;
        }

        // Check if HTML contains base64 images - if so, mark for post-paste upload
        const htmlContent = event.clipboardData?.getData("text/html");
        if (htmlContent && hasBase64Images(htmlContent)) {
          // Let Lexical handle the paste normally (it will use ImageNode.importDOM)
          // Then upload any data URL images after the paste completes
          pendingDataUrlUploadRef.current = true;
        }

        // Let Lexical handle the paste
        return false;
      },
      COMMAND_PRIORITY_HIGH
    );

    // Listen for editor updates to handle post-paste image uploads
    const unregisterUpdateListener = editor.registerUpdateListener(
      ({ tags }) => {
        // Check if this update is from a paste and we have pending uploads
        if (pendingDataUrlUploadRef.current && !tags.has("image-upload")) {
          pendingDataUrlUploadRef.current = false;
          // Use setTimeout to ensure paste is fully complete
          setTimeout(() => {
            uploadDataUrlImages();
          }, 0);
        }
      }
    );

    // Register DRAG_DROP_PASTE command
    const unregisterDragDropCommand = editor.registerCommand(
      DRAG_DROP_PASTE,
      (files: File[]) => {
        const imageFile = files.find((file) => file.type.startsWith("image/"));

        if (!imageFile) {
          return false;
        }

        handleImageUpload(imageFile);
        return true;
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      unregisterInsertCommand();
      unregisterPasteCommand();
      unregisterUpdateListener();
      unregisterDragDropCommand();
    };
  }, [editor, handleImageUpload, uploadDataUrlImages]);

  return null;
}
